type CoreVitalName = "CLS" | "INP" | "LCP";
type CoreVitalRating = "good" | "needs-improvement" | "poor";
type NavigationType =
  | "navigate"
  | "reload"
  | "back-forward"
  | "back-forward-cache"
  | "prerender"
  | "restore"
  | "soft-navigation";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    __shugertVitalsStarted?: boolean;
  }
}

type VitalReport = {
  name: CoreVitalName;
  value: number;
  delta: number;
  id: string;
  rating: CoreVitalRating;
  navigationType: NavigationType;
  entriesCount: number;
  attribution: unknown;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function safeText(value: unknown, maxLength = 180): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  return normalized ? normalized.slice(0, maxLength) : undefined;
}

function safeNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function metricTarget(name: CoreVitalName, attribution: Record<string, unknown>): string | undefined {
  if (name === "CLS") return safeText(attribution.largestShiftTarget);
  if (name === "INP") return safeText(attribution.interactionTarget);
  return safeText(attribution.target);
}

function emitWebVital(payload: Record<string, unknown>): void {
  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", "web_vital", payload);
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "web_vital", ...payload });
  } catch {
    /* Real-user monitoring must never break the page. */
  }
}

function reportVital(metric: VitalReport): void {
  const attribution = asRecord(metric.attribution);
  const normalizedValue = metric.name === "CLS"
    ? Number(metric.value.toFixed(4))
    : Math.round(metric.value);
  const normalizedDelta = metric.name === "CLS"
    ? Number(metric.delta.toFixed(4))
    : Math.round(metric.delta);

  emitWebVital({
    event_category: "Web Vitals",
    event_label: metric.name,
    value: normalizedDelta,
    metric_name: metric.name,
    metric_value: normalizedValue,
    metric_delta: normalizedDelta,
    metric_id: metric.id,
    metric_rating: metric.rating,
    metric_navigation_type: metric.navigationType,
    metric_target: metricTarget(metric.name, attribution),
    metric_load_state: safeText(attribution.loadState, 60),
    metric_interaction_type: safeText(attribution.interactionType, 60),
    metric_input_delay: safeNumber(attribution.inputDelay),
    metric_processing_duration: safeNumber(attribution.processingDuration),
    metric_presentation_delay: safeNumber(attribution.presentationDelay),
    metric_time_to_first_byte: safeNumber(attribution.timeToFirstByte),
    metric_resource_load_delay: safeNumber(attribution.resourceLoadDelay),
    metric_resource_load_duration: safeNumber(attribution.resourceLoadDuration),
    metric_element_render_delay: safeNumber(attribution.elementRenderDelay),
    metric_largest_shift_value: safeNumber(attribution.largestShiftValue),
    metric_entries_count: metric.entriesCount,
    page_path: window.location.pathname,
    page_title: document.title,
    page_locale: window.location.pathname.startsWith("/es") ? "es" : "en",
    connection_type:
      (navigator as Navigator & { connection?: { effectiveType?: string } }).connection
        ?.effectiveType,
    non_interaction: true,
  });
}

/**
 * Start official Core Web Vitals real-user monitoring once per page load.
 * The attribution build is loaded after hydration and reports no form values,
 * names, email addresses, or user-entered URLs.
 */
export function initWebVitalsMonitoring(): void {
  if (typeof window === "undefined" || window.__shugertVitalsStarted) return;
  window.__shugertVitalsStarted = true;

  void import("web-vitals/attribution")
    .then(({ onCLS, onINP, onLCP }) => {
      onCLS((metric) => {
        reportVital({
          name: metric.name,
          value: metric.value,
          delta: metric.delta,
          id: metric.id,
          rating: metric.rating,
          navigationType: metric.navigationType,
          entriesCount: metric.entries.length,
          attribution: metric.attribution,
        });
      });
      onINP((metric) => {
        reportVital({
          name: metric.name,
          value: metric.value,
          delta: metric.delta,
          id: metric.id,
          rating: metric.rating,
          navigationType: metric.navigationType,
          entriesCount: metric.entries.length,
          attribution: metric.attribution,
        });
      }, { includeProcessedEventEntries: false });
      onLCP((metric) => {
        reportVital({
          name: metric.name,
          value: metric.value,
          delta: metric.delta,
          id: metric.id,
          rating: metric.rating,
          navigationType: metric.navigationType,
          entriesCount: metric.entries.length,
          attribution: metric.attribution,
        });
      });
    })
    .catch(() => {
      /* Browsing and conversion behavior continue when RUM cannot load. */
    });
}
